import type { Component } from "solid-js";
import { lazy as solidLazy } from "solid-js";

/**
 * Thrown when a dynamic `import()` fails — typically because the tab is on an
 * older build and the hashed chunk no longer exists on the CDN. The root
 * ErrorBoundary detects this class and reloads the page so the user picks up
 * the current build.
 */
export class DynamicImportError extends Error {
  constructor(public readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "DynamicImportError";
    if (cause instanceof Error && cause.stack) {
      this.stack = cause.stack;
    }
  }
}

/**
 * Drop-in replacement for solid-js `lazy()` that tags loader failures with
 * `DynamicImportError`. Detection at the source is more reliable than pattern-
 * matching the browser's error message, which varies across Chromium, Firefox,
 * and Safari and can change between versions.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- mirror solid-js `lazy()` signature
export const lazy = <T extends Component<any>>(loader: () => Promise<{ default: T }>) =>
  solidLazy(() =>
    loader().catch((err) => {
      throw new DynamicImportError(err);
    })
  );
